import { useEffect, useState, useRef } from 'react'
import createApp from '@shopify/app-bridge'
import { Redirect } from '@shopify/app-bridge/actions'
import { getSessionToken } from '@shopify/app-bridge-utils'
import logger from '../lib/logger'

function useToken() {
  const [shopOrigin, setShopOrigin] = useState(null)
  const [host, setHost] = useState(null)
  const [error, setError] = useState(false)
  const [token, setToken] = useState(null)
  const initializedRef = useRef(false)

  useEffect(() => {
    // Prevent multiple initializations
    if (initializedRef.current) return
    initializedRef.current = true

    // Skip in debug mode
    if (process.env.REACT_APP_DEBUG_SHOPIFY_DOMAIN) {
      setShopOrigin(process.env.REACT_APP_DEBUG_SHOPIFY_DOMAIN)
      setToken('DEBUG')
      setHost(process.env.REACT_APP_DEBUG_HOST)
      return
    }

    const url = new URL(window.location.href)
    const _shopOrigin = url.searchParams.get('shop')
    const _host = url.searchParams.get('host')

    if (!_shopOrigin) {
      setError(true)
      return
    }

    if (window.top === window.self) {
      // Not embedded in Shopify, redirect to OAuth
      window.location.assign(`/auth?shop=${_shopOrigin}`)
      return
    }

    if (!_host) {
      setError(true)
      return
    }

    // Store in state
    setShopOrigin(_shopOrigin)
    setHost(_host)

    // Initialize App Bridge
    const app = createApp({
      apiKey: process.env.REACT_APP_API_KEY,
      host: _host,
    })

    // Single validation function (not in callback to avoid dependencies)
    const validateToken = async () => {
      try {
        const sessionToken = await getSessionToken(app)

        if (!sessionToken) {
          Redirect.create(app).dispatch(
            Redirect.Action.REMOTE,
            `${url.protocol}//${url.host}/auth?shop=${_shopOrigin}&host=${_host}`
          )
          return
        }

        const resp = await fetch(`/api/verify_token?shop=${_shopOrigin}`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
          credentials: 'include',
        })

        if (resp.ok) {
          const data = await resp.json()
          if (data.status === 'ok') {
            setToken(sessionToken)
            return
          }
        }

        Redirect.create(app).dispatch(
          Redirect.Action.REMOTE,
          `${url.protocol}//${url.host}/auth?shop=${_shopOrigin}&host=${_host}`
        )

      } catch (error) {
        logger.error('Token validation error:', error)
        Redirect.create(app).dispatch(
          Redirect.Action.REMOTE,
          `${url.protocol}//${url.host}/auth?shop=${_shopOrigin}&host=${_host}`
        )
      }
    }

    // Run validation once
    validateToken()

  }, []) // Empty dependency array - runs once on mount

  return { shopOrigin, host, error, token }
}

export default useToken
